import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Heart } from "lucide-react";
import Seo from "@/components/Seo";

export default function About() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="page-about">
      <Seo title="About Hola Movers — Who's Behind Your Move" path="/about"
        description="The people behind Hola Movers: why we help English-speaking US residents move to Mexico, and how we work — calm, clear, no hard sells." />
      <section className="bg-sand-100">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
          <div className="max-w-2xl animate-fade-up">
            <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-4">About us</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900 leading-[1.05]">
              Hola — we're the people behind your move
            </h1>
            <p className="mt-6 text-lg text-charcoal-600 leading-relaxed">
              Hola Movers is run from Mérida, Yucatán, by people who made this exact move themselves —
              and remember how confusing it felt from the US side.
            </p>
          </div>
        </div>
      </section>
      <div className="mx-auto max-w-3xl px-6 lg:px-12 py-16 space-y-10">
        <div className="space-y-5 text-charcoal-700 leading-relaxed" data-testid="about-story">
          <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800">Why we built this</h2>
          <p>
            When we planned our own move to Mexico, every answer lived in a different Facebook group,
            half the advice was out of date, and the other half assumed you spoke Spanish and knew a guy.
            Nobody would just tell us, plainly, what it would cost and what to do first.
          </p>
          <p>
            So that's what Hola Movers is: the calm, clear version of the move we wish we'd had.
            A real cost calculator instead of a "book a consultation to find out." A step-by-step
            timeline instead of a sales funnel. Drivers we'd trust with our own pets — because we have.
          </p>
          <p>
            We keep it low-pressure on purpose. If a $75 call answers your questions and you never need
            us again, that's a win. If you want us beside you door-to-door, we're here for that too.
          </p>
          <p className="italic text-charcoal-500">Hecho con cariño en Mérida.</p>
        </div>
        <div className="rounded-3xl bg-sage-light p-8 flex flex-wrap items-center gap-4" data-testid="about-contact">
          <MessageCircle size={22} className="text-sage-700 shrink-0" />
          <p className="flex-1 min-w-[220px] text-charcoal-700">
            Have a question before booking anything? Message us — a real person answers.
          </p>
          <Link to="/contact" className="rounded-full bg-terracotta px-6 py-2.5 text-sm text-white font-medium hover:bg-terracotta-hover transition-colors">
            Contact us
          </Link>
        </div>
        <div className="rounded-3xl border border-sand-200 bg-white p-8" data-testid="about-values">
          <Heart size={20} className="text-terracotta" />
          <h2 className="mt-3 text-xl font-medium text-charcoal-800">How we work</h2>
          <ul className="mt-4 space-y-2 text-charcoal-600 text-base leading-relaxed list-disc pl-5">
            <li>Plain answers, real prices — the calculator shows ranges before you ever talk to us.</li>
            <li>No hard sells. One step at a time, at your pace.</li>
            <li>Background-checked, bilingual drivers — <Link to="/drivers" className="text-terracotta underline underline-offset-2">meet them here</Link>.</li>
            <li>Pets are family. They get the same door-to-door care you do.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
